import React, { useMemo, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import ImageIcon from "@mui/icons-material/Image";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LinkIcon from "@mui/icons-material/Link";
import { ChamberMemberProSenderDomainStatusEnum } from "../../../../../graphql/types";
import {
  FieldLabel,
  SectionSaveReset,
  SettingsModal,
  SectionSaveResetProps,
  TextInput,
  settingsPrimaryButtonClassName,
  settingsSectionCardClassName,
  settingsSectionDescriptionClassName,
  settingsSectionTitleClassName,
  settingsSecondaryButtonClassName,
} from "../common";
import { ChamberSettingsFormState, EmailTemplate } from "../settingsData";
import EmailSubscriberTargetSection, {
  EmailSubscriberTargetValue,
} from "./EmailSubscriberTargetSection";

type EmailSettings = ChamberSettingsFormState["email"];

type TemplateDraft = {
  subject: string;
  body: string;
};

type AutomationGroup = {
  title: string;
  description: string;
  templateIds: string[];
};

type TemplateDisplayCopy = {
  title: string;
  description: string;
};

const SUPPORT_EMAIL = "support@chamberperksapp.com";

const EMAIL_GROUPS: AutomationGroup[] = [
  {
    title: "Membership Auto Emails and Configurations",
    description:
      "Preview and edit the auto email templates below which will be sent to members. Enter the email address your chamber would like to use for each automated email sent through the platform. Reply to email will default to sent email address if not entered separately.",
    templateIds: [
      "membership-confirmation",
      "payment-received",
      "membership-approved",
      "membership-declined",
      "renewal-reminder",
    ],
  },
  {
    title: "Events Auto Emails and Configurations",
    description:
      "Preview and edit the auto email templates below which will be sent to members. Enter the email address your chamber would like to use for each automated email sent through the platform. Reply to email will default to sent email address if not entered separately.",
    templateIds: [
      "event-registration-acknowledgement",
      "event-invoice-me-payment-request",
      "event-payment-receipt",
      "event-24-hour-reminder",
    ],
  },
  {
    title: "Sponsorship Auto Emails and Configurations",
    description:
      "Preview and edit the auto email templates below which will be sent to members. Enter the email address your chamber would like to use for each automated email sent through the platform. Reply to email will default to sent email address if not entered separately.",
    templateIds: [
      "event-sponsorship-request-received",
      "event-sponsorship-approval-payment-request",
      "event-sponsorship-rejection",
    ],
  },
];

const TEMPLATE_COPY: Record<string, TemplateDisplayCopy> = {
  "membership-confirmation": {
    title:
      "Membership Application Confirmation (APPROVAL REQUIRED) Auto Email",
    description:
      "Sent to a prospective member when a membership application is submitted and has not been approved and payment not received",
  },
  "payment-received": {
    title:
      "Membership Application Approved (NO APPROVAL REQUIRED) and Payment Received Auto Email",
    description:
      "Sent to a prospective member after submitting their membership application  and payment ( not requiring approval)",
  },
  "membership-approved": {
    title: "Membership Application Status Update (Approved) Auto Email",
    description:
      "Sent after your chamber or Board of Directors has approved a membership application. (Used only when approval required has been enabled by your chamber)",
  },
  "membership-declined": {
    title: "Membership Application Status Update (Declined) Auto Email",
    description:
      "Sent to a prospective member when a membership application is declined.",
  },
  "renewal-reminder": {
    title: "Membership Renewal Reminder Auto Email",
    description: "Sent to member prior to membership renewal date.",
  },
  "event-registration-acknowledgement": {
    title: "Event Registration Confirmation Auto Email",
    description: "Sent after an individual has registered for an event.",
  },
  "event-invoice-me-payment-request": {
    title:
      "Event Registration Submitted - Payment Required Request Auto Email",
    description:
      "Sent when an individual has registered for an event and requested an invoice be sent for payment.",
  },
  "event-payment-receipt": {
    title: "Event Payment Receipt Auto Email",
    description: "Sent after payment has been received",
  },
  "event-24-hour-reminder": {
    title: "24-Hour Event Reminder",
    description:
      "Sent 24 hrs before event to all individuals registered for the event",
  },
  "event-sponsorship-request-received": {
    title: "Sponsorship Request Received Auto Email",
    description:
      "Sent to a member who has requested an event sponsorship",
  },
  "event-sponsorship-approval-payment-request": {
    title: "Sponsorship Approval/Payment Request Auto Email",
    description:
      "Sent to a member to confirm their sponsorship request has been approved for {EventName}",
  },
  "event-sponsorship-rejection": {
    title: "Sponsorship Rejection Auto Email",
    description:
      "Sent to a member when their sponsorship request can not be fulfilled",
  },
};

const toolbarButtons = [
  { label: "Bold", icon: <FormatBoldIcon fontSize="small" /> },
  { label: "Italic", icon: <FormatItalicIcon fontSize="small" /> },
  { label: "Underline", icon: <FormatUnderlinedIcon fontSize="small" /> },
  { label: "Align left", icon: <FormatAlignLeftIcon fontSize="small" /> },
  { label: "Align center", icon: <FormatAlignCenterIcon fontSize="small" /> },
  { label: "Align right", icon: <FormatAlignRightIcon fontSize="small" /> },
  { label: "Bulleted list", icon: <FormatListBulletedIcon fontSize="small" /> },
  { label: "Numbered list", icon: <FormatListNumberedIcon fontSize="small" /> },
  { label: "Insert image", icon: <ImageIcon fontSize="small" /> },
  { label: "Insert link", icon: <LinkIcon fontSize="small" /> },
];

const getTemplateDisplayCopy = (template: EmailTemplate) =>
  TEMPLATE_COPY[template.id] || {
    title: template.title,
    description: template.description,
  };

const normalizeSenderEmailInput = (email: string) =>
  email.trim().toLowerCase();

const isValidSenderEmailInput = (email: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizeSenderEmailInput(email));

const getUniqueSenderEmails = (emails: string[]) =>
  Array.from(
    new Set(
      emails
        .map(normalizeSenderEmailInput)
        .filter(Boolean)
    )
  );

// B15 parity fix: no seconds — the screenshots show "Last Edited May 28, 2026
// at 3:16 PM" and every sibling "Last Saved" label on this page uses
// timeStyle: "short" (minutes precision).
const lastEditedDateFormatter = new Intl.DateTimeFormat("en-US", {
  month: "long",
  day: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
});

const getTemplateLastEditedLabel = (updatedAtUtcMilli?: number | null) => {
  if (
    typeof updatedAtUtcMilli !== "number" ||
    !Number.isFinite(updatedAtUtcMilli) ||
    updatedAtUtcMilli <= 0
  ) {
    return "Last Edited Not saved yet";
  }

  return `Last Edited ${lastEditedDateFormatter.format(
    new Date(updatedAtUtcMilli)
  )}`;
};

const EmailTab = ({
  chamberId,
  settings,
  onChange,
  subscriberTarget,
  onSubscriberTargetDirtyChange,
  sectionControls,
}: {
  chamberId: string;
  settings: EmailSettings;
  onChange: (settings: EmailSettings) => void;
  subscriberTarget: EmailSubscriberTargetValue;
  onSubscriberTargetDirtyChange: (isDirty: boolean) => void;
  sectionControls: SectionSaveResetProps;
}) => {
  const [senderTemplateId, setSenderTemplateId] = useState<string | null>(null);
  const [customSenderEmailInput, setCustomSenderEmailInput] = useState("");
  const [customSenderEmailError, setCustomSenderEmailError] = useState("");
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(
    null
  );
  const [templateDraft, setTemplateDraft] = useState<TemplateDraft | null>(
    null
  );
  const [showUnsavedTemplateWarning, setShowUnsavedTemplateWarning] =
    useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [automationRequest, setAutomationRequest] = useState("");

  const { senderEmail, enabledSenderEmails, emailTemplates, senderDomain } =
    settings;
  const enabledSenderEmailOptions = getUniqueSenderEmails(enabledSenderEmails);
  const senderDomainConfigured =
    senderDomain.status !== ChamberMemberProSenderDomainStatusEnum.NotConfigured;

  const templatesById = useMemo(
    () =>
      emailTemplates.reduce<Record<string, EmailTemplate>>((acc, template) => {
        acc[template.id] = template;
        return acc;
      }, {}),
    [emailTemplates]
  );

  const selectedSenderTemplate = senderTemplateId
    ? templatesById[senderTemplateId]
    : null;
  const selectedEditingTemplate = editingTemplateId
    ? templatesById[editingTemplateId]
    : null;
  const templateDraftIsDirty =
    !!selectedEditingTemplate &&
    !!templateDraft &&
    (templateDraft.subject !== selectedEditingTemplate.subject ||
      templateDraft.body !== selectedEditingTemplate.body);

  const emailBelongsToSenderDomain = (email: string) =>
    !senderDomain.domain ||
    email.trim().toLowerCase().endsWith(`@${senderDomain.domain}`);

  const getSenderEmailValidationError = (email: string) => {
    const normalizedEmail = normalizeSenderEmailInput(email);

    if (!normalizedEmail) {
      return "Enter an email address.";
    }

    if (!isValidSenderEmailInput(normalizedEmail)) {
      return "Enter a valid email address.";
    }

    if (senderDomainConfigured && !emailBelongsToSenderDomain(normalizedEmail)) {
      return `Use an email address ending in @${senderDomain.domain}.`;
    }

    return "";
  };

  const updateEmailTemplate = (
    automationId: string,
    updater: (template: EmailTemplate) => EmailTemplate
  ) => {
    onChange({
      ...settings,
      emailTemplates: emailTemplates.map((template) =>
        template.id === automationId ? updater(template) : template
      ),
    });
  };

  const updateTemplateSenderEmail = (
    automationId: string,
    nextSenderEmail: string | null
  ) => {
    const normalizedSenderEmail = nextSenderEmail
      ? normalizeSenderEmailInput(nextSenderEmail)
      : null;

    onChange({
      ...settings,
      enabledSenderEmails: normalizedSenderEmail
        ? getUniqueSenderEmails([...enabledSenderEmails, normalizedSenderEmail])
        : enabledSenderEmails,
      emailTemplates: emailTemplates.map((template) =>
        template.id === automationId
          ? {
              ...template,
              senderEmail: normalizedSenderEmail,
              updatedAtUtcMilli: Date.now(),
            }
          : template
      ),
    });
  };

  const openSenderEditor = (template: EmailTemplate) => {
    setSenderTemplateId(template.id);
    setCustomSenderEmailInput(template.senderEmail || "");
    setCustomSenderEmailError("");
  };

  const closeSenderEditor = () => {
    setSenderTemplateId(null);
    setCustomSenderEmailInput("");
    setCustomSenderEmailError("");
  };

  const saveCustomSenderEmail = () => {
    if (!selectedSenderTemplate) {
      return;
    }

    const validationError = getSenderEmailValidationError(
      customSenderEmailInput
    );

    if (validationError) {
      setCustomSenderEmailError(validationError);
      return;
    }

    updateTemplateSenderEmail(
      selectedSenderTemplate.id,
      normalizeSenderEmailInput(customSenderEmailInput)
    );
    closeSenderEditor();
  };

  const openTemplateEditor = (template: EmailTemplate) => {
    setEditingTemplateId(template.id);
    setTemplateDraft({
      subject: template.subject,
      body: template.body,
    });
    setShowUnsavedTemplateWarning(false);
  };

  const closeTemplateEditor = () => {
    setEditingTemplateId(null);
    setTemplateDraft(null);
    setShowUnsavedTemplateWarning(false);
  };

  const leaveTemplateEditorWithoutSaving = () => {
    setShowUnsavedTemplateWarning(false);
    setEditingTemplateId(null);
    setTemplateDraft(null);
  };

  const requestCloseTemplateEditor = () => {
    if (templateDraftIsDirty) {
      setShowUnsavedTemplateWarning(true);
      return;
    }

    closeTemplateEditor();
  };

  const saveTemplateDraft = () => {
    if (!selectedEditingTemplate || !templateDraft) {
      return;
    }

    // B15 guardrail: saving an unchanged template must not dirty the email
    // section with a timestamp-only diff — just close the editor.
    if (!templateDraftIsDirty) {
      closeTemplateEditor();
      return;
    }

    updateEmailTemplate(selectedEditingTemplate.id, (template) => ({
      ...template,
      subject: templateDraft.subject,
      body: templateDraft.body,
      updatedAtUtcMilli: Date.now(),
    }));
    closeTemplateEditor();
  };

  const openAutomationRequestEmail = () => {
    const bodyText = automationRequest.trim()
      ? `New email automation request:\n\n${automationRequest.trim()}`
      : "New email automation request:";

    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
      "New email automation request"
    )}&body=${encodeURIComponent(bodyText)}`;
    setContactModalOpen(false);
    setAutomationRequest("");
  };

  const renderContactRow = () => (
    <button
      type="button"
      onClick={() => setContactModalOpen(true)}
      className="mt-5 flex w-full items-center justify-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 text-center text-sm font-semibold text-slate-700 transition hover:border-cpaGold hover:bg-amber-50"
    >
      <span className="text-xl font-light leading-none text-slate-700">
        +
      </span>
      <span>
        If your chamber would like to add a new email automation please contact
        support
      </span>
    </button>
  );

  const renderEmailAutomation = (automation: EmailTemplate) => {
    const displayCopy = getTemplateDisplayCopy(automation);
    const selectedSenderEmail =
      automation.senderEmail || senderEmail || "No sender selected";

    return (
      <div
        key={automation.id}
        className="min-h-[282px] rounded-lg bg-slate-50 px-5 py-5 sm:px-6 sm:py-6 lg:min-h-[308px]"
      >
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex min-w-0 items-start gap-5">
            <EmailOutlinedIcon
              className="mt-1 shrink-0 text-gray-950"
              sx={{ fontSize: 28 }}
            />
            <div className="min-w-0">
              <h3 className="text-base font-bold leading-tight text-black">
                {displayCopy.title}
              </h3>
              <p className="mt-1 max-w-[1050px] text-sm leading-6 text-black">
                {displayCopy.description}
              </p>
            </div>
          </div>
          <div className="shrink-0 lg:text-right">
            <button
              type="button"
              onClick={() => openTemplateEditor(automation)}
              className="inline-flex h-9 items-center justify-center gap-3 rounded-md border border-cpaGold bg-cpaGold px-5 text-sm font-bold text-white transition hover:bg-amber-500"
            >
              <EditOutlinedIcon fontSize="small" />
              Edit Automated Email Template
            </button>
            <p className="mt-1 text-sm text-gray-500">
              {getTemplateLastEditedLabel(automation.updatedAtUtcMilli)}
            </p>
          </div>
        </div>

        <div className="mt-[60px] lg:mt-[72px]">
          <p className="text-base font-bold leading-6 text-black">
            <span className="mr-5">Subject:</span>
            <span>{automation.subject}</span>
          </p>
        </div>

        <div className="mt-12 max-w-[760px]">
          <span className="block text-sm font-bold uppercase text-black">
            Email Sent From
          </span>
          <div className="mt-2 flex min-h-[48px] items-center gap-3 rounded-lg bg-white px-4 py-2">
            <span className="min-w-0 flex-1 truncate text-base text-black underline">
              {selectedSenderEmail}
            </span>
            <button
              type="button"
              onClick={() => openSenderEditor(automation)}
              className="inline-flex h-8 shrink-0 items-center justify-center rounded-md border border-cpaGold bg-cpaGold px-5 text-sm font-bold text-white transition hover:bg-amber-500"
            >
              Update
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-14">
      <EmailSubscriberTargetSection
        chamberId={chamberId}
        initialTarget={subscriberTarget}
        onDirtyChange={onSubscriberTargetDirtyChange}
      />

      {EMAIL_GROUPS.map((group) => {
        const groupTemplates = group.templateIds
          .map((templateId) => templatesById[templateId])
          .filter((template): template is EmailTemplate => Boolean(template));

        if (groupTemplates.length === 0) {
          return null;
        }

        return (
          <section
            key={group.title}
            className={settingsSectionCardClassName}
          >
            <div>
              <h2 className={settingsSectionTitleClassName}>
                {group.title}
              </h2>
              <p className={settingsSectionDescriptionClassName}>
                {group.description}
              </p>
            </div>

            <div className="mt-7 space-y-5">
              <div className="space-y-5">
                {groupTemplates.map(renderEmailAutomation)}
              </div>
              {renderContactRow()}
              <SectionSaveReset {...sectionControls} />
            </div>
          </section>
        );
      })}

      {selectedSenderTemplate && (
        <SettingsModal
          title="Update Email Sender"
          description="Choose the sender email for this automation. Leave it on the global sender when this email does not need its own sender."
          onClose={closeSenderEditor}
          panelClassName="max-w-2xl rounded-lg"
        >
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => {
                updateTemplateSenderEmail(selectedSenderTemplate.id, null);
                closeSenderEditor();
              }}
              className={`flex w-full items-center justify-between rounded-lg border px-4 py-3 text-left transition ${
                !selectedSenderTemplate.senderEmail
                  ? "border-cpaGold bg-amber-50"
                  : "border-gray-200 bg-white hover:border-gray-300"
              }`}
            >
              <span className="text-sm font-semibold text-gray-950">
                Use global sender
              </span>
              <span className="text-sm text-gray-600">
                {senderEmail || "No global sender selected"}
              </span>
            </button>

            {enabledSenderEmailOptions.length === 0 ? (
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                <p className="text-sm font-semibold text-gray-950">
                  No enabled sender emails are available.
                </p>
              </div>
            ) : (
              enabledSenderEmailOptions.map((email) => {
                const blockedByDomain =
                  senderDomainConfigured && !emailBelongsToSenderDomain(email);

                return (
                  <button
                    key={email}
                    type="button"
                    disabled={blockedByDomain}
                    onClick={() => {
                      updateTemplateSenderEmail(selectedSenderTemplate.id, email);
                      closeSenderEditor();
                    }}
                    className={`flex w-full items-center justify-between rounded-lg border px-4 py-3 text-left transition disabled:cursor-not-allowed disabled:opacity-50 ${
                      selectedSenderTemplate.senderEmail === email
                        ? "border-cpaGold bg-amber-50"
                        : "border-gray-200 bg-white hover:border-gray-300"
                    }`}
                  >
                    <span className="text-sm font-semibold text-gray-950">
                      {email}
                    </span>
                    <span className="text-sm font-semibold text-cpaGold">
                      {selectedSenderTemplate.senderEmail === email
                        ? "Selected"
                        : blockedByDomain
                        ? "Different domain"
                        : ""}
                    </span>
                  </button>
                );
              })
            )}

            <div className="rounded-lg border border-gray-200 bg-white px-4 py-4">
              <FieldLabel label="Use a Different Email Address">
                <div className="flex flex-col gap-3 sm:flex-row">
                  <TextInput
                    value={customSenderEmailInput}
                    onChange={(email) => {
                      setCustomSenderEmailInput(email);
                      setCustomSenderEmailError("");
                    }}
                    placeholder="name@yourdomain.com"
                  />
                  <button
                    type="button"
                    onClick={saveCustomSenderEmail}
                    className={`${settingsPrimaryButtonClassName} h-11 shrink-0 px-5 text-sm`}
                  >
                    Use Sender
                  </button>
                </div>
                {customSenderEmailError && (
                  <p className="mt-2 text-sm font-semibold text-red-600">
                    {customSenderEmailError}
                  </p>
                )}
              </FieldLabel>
            </div>
          </div>
        </SettingsModal>
      )}

      {contactModalOpen && (
        <SettingsModal
          title="Request New Email Automation"
          description={`Tell us what automated email your chamber needs. This will open an email addressed to ${SUPPORT_EMAIL}.`}
          onClose={() => setContactModalOpen(false)}
          panelClassName="max-w-2xl"
        >
          <div className="space-y-5">
            <FieldLabel label="Request Details">
              <textarea
                value={automationRequest}
                onChange={(event) => setAutomationRequest(event.target.value)}
                placeholder="Describe the email automation you would like to add."
                className="min-h-[160px] w-full resize-y rounded-lg border border-gray-300 bg-white px-3 py-3 text-base text-gray-900 shadow-sm outline-none transition focus:border-cpaGold focus:ring-2 focus:ring-amber-100"
              />
            </FieldLabel>
            <div className="flex flex-col justify-end gap-3 border-t border-gray-200 pt-5 sm:flex-row">
              <button
                type="button"
                onClick={() => setContactModalOpen(false)}
                className={`${settingsSecondaryButtonClassName} min-w-[140px]`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={openAutomationRequestEmail}
                className={`${settingsPrimaryButtonClassName} min-w-[200px]`}
              >
                Email Support
              </button>
            </div>
          </div>
        </SettingsModal>
      )}

      {selectedEditingTemplate && templateDraft && (
        <SettingsModal
          title="Edit Auto Email Template"
          description="Update the subject and body used by this automation."
          onClose={requestCloseTemplateEditor}
          panelClassName="max-w-4xl"
          bodyClassName="max-h-[72vh] overflow-y-auto"
        >
          <div className="space-y-5">
            <FieldLabel label="Auto Email Title (cannot be edited)">
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-base font-bold text-cpaBlue">
                {getTemplateDisplayCopy(selectedEditingTemplate).title}
              </div>
            </FieldLabel>

            <FieldLabel label="Email Subject">
              <TextInput
                value={templateDraft.subject}
                onChange={(subject) =>
                  setTemplateDraft((draft) =>
                    draft ? { ...draft, subject } : draft
                  )
                }
                placeholder="Subject Here"
              />
            </FieldLabel>

            <div>
              <span className="block text-sm font-semibold text-gray-950">
                Email Body
              </span>
              <div className="mt-2 overflow-hidden rounded-lg border border-gray-200 bg-white">
                <div className="flex flex-wrap items-center gap-1 border-b border-gray-200 bg-gray-50 px-3 py-2">
                  {toolbarButtons.map((button) => (
                    <button
                      key={button.label}
                      type="button"
                      aria-label={button.label}
                      title={button.label}
                      className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-900 transition hover:bg-white"
                    >
                      {button.icon}
                    </button>
                  ))}
                </div>
                <textarea
                  value={templateDraft.body}
                  onChange={(event) =>
                    setTemplateDraft((draft) =>
                      draft ? { ...draft, body: event.target.value } : draft
                    )
                  }
                  placeholder="Email Content Here"
                  className="min-h-[260px] max-h-[320px] w-full resize-y border-0 px-6 py-5 text-base text-gray-900 outline-none"
                />
              </div>
            </div>

            <div className="flex flex-col justify-end gap-3 border-t border-gray-200 pt-5 sm:flex-row">
              <button
                type="button"
                onClick={requestCloseTemplateEditor}
                className={`${settingsSecondaryButtonClassName} min-w-[160px]`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveTemplateDraft}
                className={`${settingsPrimaryButtonClassName} min-w-[220px]`}
              >
                Save Auto Email Template
              </button>
            </div>
          </div>
        </SettingsModal>
      )}

      {showUnsavedTemplateWarning && (
        <div className="fixed inset-0 z-[10000] flex items-end justify-center bg-black/40 px-4 py-8 md:items-center">
          <div className="relative w-full max-w-xl rounded-lg border border-gray-200 bg-white px-8 py-8 text-center shadow-xl">
            <button
              type="button"
              onClick={() => setShowUnsavedTemplateWarning(false)}
              className="absolute right-5 top-5 flex h-10 w-10 items-center justify-center rounded-full bg-red-600 text-white transition hover:bg-red-700"
              aria-label="Close unsaved changes warning"
            >
              <CloseIcon />
            </button>
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-cpaGold text-white">
              <InfoOutlinedIcon sx={{ fontSize: 48 }} />
            </div>
            <h3 className="mt-6 text-xl font-bold text-gray-950">
              UNSAVED CHANGES
            </h3>
            <p className="mt-4 text-base text-gray-950">
              You have unsaved changes.
            </p>
            <p className="mt-2 text-base text-gray-950">
              Click <span className="font-bold">Go Back</span> to save changes.
            </p>
            <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => setShowUnsavedTemplateWarning(false)}
                className={settingsPrimaryButtonClassName}
              >
                Go Back
              </button>
              <button
                type="button"
                onClick={leaveTemplateEditorWithoutSaving}
                className="inline-flex h-11 items-center justify-center rounded-lg border border-gray-900 bg-gray-900 px-4 text-sm font-semibold text-white transition hover:bg-black"
              >
                Leave Without Saving
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailTab;
